/* The ids row, 2b-2 · the editors' writes. With an API (VITE_API_URL) what an editor changes — a gift type, a roue, a
   demande, a platform, a network, the establishment's name, booking link and window, the fiche, the sector, the identity
   fields, the look, the day-one model — is the server's (contracts 02, 03, 04). The setters of lib/wheel, lib/asks,
   lib/campaign, lib/scope and lib/models change this tab's copy (lib/mirror) at once and say what changed (`edited`); this
   module sends it, one write at a time, in the order asked and after the tab's other writes (`queueWrite`, the drafts'
   queue), each sending the copy as it is when its turn comes, and puts the server's answer in its place unless the copy
   changed again meanwhile (its own write follows). Something made in this tab gets the server's id, and its first id keeps
   finding it (`serverIdOf`). A refusal reads back from the server what it concerned (that gift, that roue, those fields),
   so the tab shows what the server holds of it — unless a write asked since will send it: that write keeps the tab's copy,
   and its page hears its own answer (R1). The refusal is kept for the page that asked: `settled(mark)` answers it there;
   one no page asks for is said by <ServerWrites/>. The demo (no VITE_API_URL) never comes here: its setters write the
   world's stores, as before. */
import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useToast } from '@/ui'
import { ApiError, apiWrites, call, get } from './api'
import {
  homeLocationId, mirror, queueWrite, serverIdOf, serverLocationId, setEditSink, setMirror,
  type Edit, type Mirror, type ServerLocation, type ServerWorkspace,
} from './mirror'
import {
  askOf, categoryOf, destinationOf, ficheOf, linkAskOf, locationOf, placeOf, roueOf, socialOf, tagOf, typeOf, workspaceOf,
  type AppAsk, type AppCategory, type AppFiche, type AppGiftType, type AppPlace, type AppPlatform, type AppRoue, type AppSocial, type AppTag, type AppWorkspace,
} from './server'
import { readCampaign, type CampaignRow } from './campaign-api'
import { noteItemTags, TAG_CAP_LINE, TAG_NAME_LONG, TAG_NAME_TAKEN, TAG_NAME_TIES_GIFT, TAG_PER_ITEM_LINE, TAG_ROLE_LINE, type TagKind } from './tags'
import type { AppSupport } from './api'
import {
  CATEGORY_CAP_LINE, CATEGORY_NAME_LONG, CATEGORY_NAME_TAKEN, CATEGORY_NAME_TIES_GIFT, CATEGORY_ROLE_LINE,
  PLACE_CAP_LINE, PLACE_NAME_LONG, PLACE_NAME_TAKEN, PLACE_ROLE_LINE, noteCampaignCategory, noteSupportPlace,
} from './organisation'
import { ROUE_MIN_PCT, roueReveal, typeCategory, type GiftType, type Roue } from './wheel'
import { NAME_TIES_GIFT, type Ask, type LinkAsk } from './asks'
import { FICHE_NOT_GOOGLE, REVIEW_URL_INVALID, REVIEW_URL_NOT_GOOGLE, type DestinationRef, type SocialRef } from './campaign'
import { sectorFloor, type SecteurId } from './scope'
import { MODELS } from './models'

const enc = encodeURIComponent
/** An id the server gave (`rw_…`, `wh_…`, `ask_…`): anything else was made in this tab and is not on the server yet. */
const onServer = (id: string): boolean => /^[a-z]+_/.test(id)

/* ---------- one write at a time; a refusal is read back and kept for the page that asked ---------- */
let asked = 0
/** What each write asked and not answered yet concerns (`kind:id`, the id as the setter named it). */
const pending = new Map<number, string[]>()
let running = 0
const refusals: { n: number; error: unknown; said: boolean }[] = []
/** What the refused write concerned (its first `kind`): the refusal line names cadeaux, roues or demandes by it. */
const kindOf = new WeakMap<object, string>()
const heard = new Set<() => void>()
let qc: QueryClient | null = null
function send(concerns: string[], run: () => Promise<void>, readBack: () => Promise<void>): void {
  const n = ++asked
  pending.set(n, concerns)
  void queueWrite(async () => {
    running = n
    try { await run() } catch (error) {
      await readBack().catch(() => undefined)
      if (error && typeof error === 'object') kindOf.set(error, concerns[0]?.split(':')[0] ?? '')
      refusals.push({ n, error, said: false })
      heard.forEach(l => l())
    } finally { pending.delete(n) }
  })
}
/** A write asked after the one running is still to send this: what the tab holds of it stays for that write (R1). */
function later(kind: string, id: string): boolean {
  const key = `${kind}:${serverIdOf(id)}`
  const keyOf = (c: string) => { const i = c.indexOf(':'); return `${c.slice(0, i)}:${serverIdOf(c.slice(i + 1))}` }
  for (const [m, cs] of pending) if (m > running && cs.some(c => keyOf(c) === key)) return true
  return false
}
/** Where the writes stand now: pass it to `settled` after the setters to hear only what they asked. */
export const writeMark = (): number => asked
/** Once every write asked so far has answered: the first refusal among those asked after `mark` (then read back from the
    server), or null. The demo answers null at once. */
export async function settled(mark: number): Promise<unknown | null> {
  if (!apiWrites) return null
  await queueWrite(async () => undefined)
  const mine = refusals.filter(r => r.n > mark)
  mine.forEach(r => { r.said = true })
  return mine[0]?.error ?? null
}

type Toast = Parameters<ReturnType<typeof useToast>>[0]
/** A page's « ✓ » once the server has taken every write it asked since `mark` — at once in the demo — or, refused, the
    refusal's own line (after `onRefused`, which re-reads what the page shows: the server's copy is back in the mirror). */
export function toastWhenSaved(mark: number, toast: (t: Toast) => void, ok: Toast | string, onRefused?: () => void): void {
  const said = typeof ok === 'string' ? { message: ok } : ok
  if (!apiWrites) { toast(said); return }
  void settled(mark).then(no => {
    if (!no) { toast(said); return }
    onRefused?.(); toast({ message: editRefusalLines(no)[0] })
  })
}

/* ---------- the reads a refusal or an answer needs: never over what a write asked since will send (R1) ---------- */
const fetchTypes = async () => (await get<AppGiftType[]>('/app/gift-types')).map(typeOf)
const fetchRoues = async () => (await get<AppRoue[]>('/app/roues')).map(roueOf)
const fetchAsks = async () => (await get<{ asks: AppAsk[] }>('/app/asks')).asks.map(askOf)
/** One item read back into the tab's list: the server's copy in its place; out of the list when the server holds none (a
    POST refused, a thing gone); back where the server lists it when the tab had let it go (an archive refused). */
function one<T extends { id: string }>(server: T[], tab: T[], id0: string): T[] {
  const id = serverIdOf(id0), srv = server.find(x => x.id === id), at = tab.findIndex(x => x.id === id)
  if (!srv) return at < 0 ? tab : tab.filter((_, i) => i !== at)
  if (at >= 0) return tab.map((x, i) => (i === at ? srv : x))
  const after = new Set(server.slice(server.indexOf(srv) + 1).map(x => x.id))
  const i = tab.findIndex(x => after.has(x.id))
  return i < 0 ? [...tab, srv] : [...tab.slice(0, i), srv, ...tab.slice(i)]
}
/** A whole list read again (the day-one model made things on the server): the server's copies, but the tab's for what a
    write asked since will send, and what the tab made that is still on its way there. */
const merged = <T extends { id: string }>(kind: string, server: T[], tab: T[]): T[] => [
  ...server.flatMap(s => (later(kind, s.id) ? tab.filter(x => x.id === s.id) : [s])),
  ...tab.filter(x => !onServer(x.id) || (later(kind, x.id) && !server.some(s => s.id === x.id))),
]
/** A refused gift or demande, read back. (A write asked since stays asked: what it will send is known before reading.) */
async function backType(id: string) { if (later('type', id)) return; const server = await fetchTypes(); setMirror({ types: one(server, mirror().types, id) }) }
async function backAsk(id: string) { if (later('ask', id)) return; const server = await fetchAsks(); setMirror({ asks: one(server, mirror().asks, id) }) }
/** The roues a sync could not send, read back; `held` takes what the server holds of them. */
async function backRoues(ids: string[]) {
  const server = await fetchRoues()
  let roues = mirror().roues
  for (const id0 of ids) {
    const id = serverIdOf(id0), srv = server.find(w => w.id === id)
    if (srv) held?.set(id, heldOf(srv)); else held?.delete(id)
    if (!later('roue', id0)) roues = one(server, roues, id0)
  }
  setMirror({ roues })
}
const readTypes = async () => { const server = await fetchTypes(); setMirror({ types: merged('type', server, mirror().types) }) }
const readAsks = async () => { const server = await fetchAsks(); setMirror({ asks: merged('ask', server, mirror().asks) }) }
async function readRoues() {
  const server = await fetchRoues()
  held = new Map(server.map(w => [w.id, heldOf(w)]))
  setMirror({ roues: merged('roue', server, mirror().roues) })
}
/** A refused link of one establishment (a platform, a network), read back: the server's rows for it in the tab's place. */
function swap<T>(tab: T[], mine: (x: T) => boolean, back: T[]): T[] {
  const at = tab.findIndex(mine), rest = tab.filter(x => !mine(x))
  return at < 0 ? [...rest, ...back] : [...rest.slice(0, at), ...back, ...rest.slice(at)]
}
async function backPlatform(locationId: string, platform: string) {
  if (later('platform', `${locationId}:${platform}`)) return
  const mine = (x: DestinationRef) => x.locationId === locationId && x.platform === platform
  const server = (await get<AppPlatform[]>('/app/platforms')).map(destinationOf)
  setMirror({ destinations: swap(mirror().destinations, mine, server.filter(mine)) })
}
async function backSocial(locationId: string, social: string) {
  if (later('social', `${locationId}:${social}`)) return
  const mine = (x: SocialRef) => x.locationId === locationId && x.social === social
  const server = (await get<AppSocial[]>('/app/socials')).map(socialOf)
  setMirror({ socials: swap(mirror().socials, mine, server.filter(mine)) })
}
/** Every link read again (a fiche read names Google's): the server's, but the tab's for a link a write asked since will send. */
async function readPlatforms() {
  const server = (await get<AppPlatform[]>('/app/platforms')).map(destinationOf)
  const kept = (x: DestinationRef) => later('platform', `${x.locationId}:${x.platform}`)
  setMirror({ destinations: [...server.filter(x => !kept(x)), ...mirror().destinations.filter(kept)] })
}
type LocationField = Extract<Edit, { kind: 'location' }>['fields'][number]
type WorkspaceField = Extract<Edit, { kind: 'workspace' }>['fields'][number]
const pick = <T extends object, K extends keyof T>(x: T, keys: readonly K[]): Partial<T> => Object.fromEntries(keys.map(k => [k, x[k]])) as unknown as Partial<T>
/** The establishments, into the mirror and into ['locations'] (useLocations), which the root seeded from it: the server's,
    but the tab's value of a field a write asked since will send. `only`: a refused write's establishment and fields; the
    rest stays as the tab holds it. */
const TAB_FIELDS: readonly LocationField[] = ['name', 'address', 'bookingUrl', 'windowDays', 'reviewUrl']
async function readLocations(only?: { id: string; fields: readonly LocationField[] }) {
  const server = (await get<ServerLocation[]>('/app/locations')).map(locationOf), tab = mirror().locations
  const waits = (id: string) => (f: LocationField) => later('location', `${id}:${f}`)
  const locations = only
    ? tab.map(l => { const s = server.find(x => x.id === l.id); return l.id !== only.id || !s ? l : { ...l, status: s.status, rating: s.rating, ...pick(s, only.fields.filter(f => !waits(l.id)(f))) } })
    : server.map(s => { const t = tab.find(x => x.id === s.id); return t ? { ...s, ...pick(t, TAB_FIELDS.filter(waits(s.id))) } : s })
  setMirror({ locations }); qc?.setQueryData(['locations'], locations)
}
/** The workspace's fields (`only`: a refused write's), the server's, but the tab's for a field a write asked since will send. */
async function readWorkspace(only: readonly WorkspaceField[] = ['sector', 'identityFields', 'wheelDesign']) {
  const s = workspaceOf(await get<AppWorkspace>('/app/workspace')), t = mirror().workspace
  setMirror({ workspace: t ? { ...t, ...pick(s, only.filter(f => !later('workspace', f))) } : s })
}
async function readFiche(locationId: string) {
  const l = mirror().locations.find(x => x.id === locationId)
  if (!l || later('fiche', locationId)) return
  setMirror({ fiches: { ...mirror().fiches, [locationId]: ficheOf(await get<AppFiche>(`/app/locations/${enc(locationId)}/fiche`), l) } })
}

/* ---------- a local id becomes the server's ---------- */
/** Everything the mirror holds under `from` now reads `to`; `from` keeps finding it (serverIdOf). */
function rekey(from: string, to: string) {
  if (from === to) return
  const m = mirror(), id = (x: string) => (x === from ? to : x)
  setMirror({
    aliases: { ...m.aliases, [from]: to },
    types: m.types.map(t => (t.id === from ? { ...t, id: to } : t)),
    roues: m.roues.map(w => (w.id === from || w.rewards.some(x => x.rewardId === from)
      ? { ...w, id: id(w.id), rewards: w.rewards.map(x => ({ ...x, rewardId: id(x.rewardId) })) } : w)),
    asks: m.asks.map(a => (a.id === from ? { ...a, id: to } : a)),
    activeRoueId: m.activeRoueId && id(m.activeRoueId),
  })
}
const put = <T extends { id: string }>(list: T[], row: T): T[] => (list.some(x => x.id === row.id) ? list.map(x => (x.id === row.id ? row : x)) : [...list, row])
/** The answer takes the place of the copy that was sent — unless the copy changed while it was on its way: then the change
    stays, and its own write (already in the queue) sends it. */
function answer<K extends 'types' | 'asks' | 'roues'>(key: K, sent: Mirror[K][number], row: Mirror[K][number]) {
  const list = mirror()[key] as Array<{ id: string }>
  const cur = list.find(x => x.id === row.id)
  if (!cur || cur === sent) setMirror({ [key]: put(list, row) } as Partial<Mirror>)
}

/* ---------- what the server takes (contract 03 §3.1–3.2, 04 §4.1–4.3, 02 §2.1–2.4) ---------- */
/** A gift type. « Comment l’utiliser » (the app's `links`: caisse, code, automatique) is no field of the server's: it is
    not sent (a gap, said in the 2b-2 HANDOFF); its `links` are the gift's web links, which the app does not edit. */
const typeBody = (t: GiftType) => ({
  name: t.name.trim(), internal: t.internal?.trim() || null, costCents: Math.round((t.cost || 0) * 100),
  validityDays: t.validityDays || null, condition: t.condition?.trim() || null, icon: t.icon ?? null,
  category: typeCategory(t), sameDay: !!t.sameDay,
})

/** The floor the server keeps (SLICE_FLOOR, D-5 amendment): 0,5 % of a roue, in tenths of a percent. */
export const SLICE_FLOOR = ROUE_MIN_PCT * 10
/** A roue's weights as the server keeps them: whole tenths of a percent summing to exactly 1 000, the shares the app shows
    kept by largest remainder. A slice the merchant gave a chance never goes under SLICE_FLOOR: what it lacks is taken from
    the largest. A slice at 0 stays 0, and the server refuses it (unwinnable_slice), as the editor does. */
export function tenths(ws: number[]): number[] {
  const total = ws.reduce((a, b) => a + Math.max(0, b), 0)
  if (total <= 0) return ws.map(() => 0)
  const raw = ws.map(w => (Math.max(0, w) / total) * 1000)
  const out = raw.map(Math.floor)
  let left = 1000 - out.reduce((a, b) => a + b, 0)
  raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1])
    .forEach(([, i]) => { if (left > 0) { out[i]++; left-- } })
  ws.forEach((w, i) => {
    if (w > 0 && out[i] < SLICE_FLOOR) {
      const big = out.reduce((b, x, j) => (x > out[b] ? j : b), 0)
      out[big] -= SLICE_FLOOR - out[i]; out[i] = SLICE_FLOOR
    }
  })
  return out
}
/** A roue: where it plays (an establishment roue with none named plays at the first establishment), its slices, its
    reveal, its texts and its own look. `usedBy` and `pending` are the server's to say. */
function roueBody(w: Roue) {
  const reveal = roueReveal(w)
  const weights = tenths(w.rewards.map(x => x.weight))
  const locs = w.scope === 'group' ? w.locations ?? [] : w.locations?.length ? w.locations : [homeLocationId()]
  return {
    name: w.name.trim(), scope: w.scope, locations: [...new Set(locs.map(l => serverLocationId(l)))],
    rewards: w.rewards.map((x, i) => ({ rewardId: serverIdOf(x.rewardId), weight: weights[i], cap: x.cap ?? null })),
    reveal: { moment: reveal.moment, countdown: reveal.countdown, earlyOpen: reveal.earlyOpen },
    texts: w.texts && Object.keys(w.texts).length ? w.texts : null, design: w.design ?? null,
  }
}
/** What the server holds of each roue, as roueBody writes it: a roue whose body reads the same is not sent again. */
let held: Map<string, string> | null = null
const heldOf = (w: Roue): string => JSON.stringify(roueBody(w))

/** A demande: its kind's fields (its kind never changes on the server), its name, category and end message. The gift a
    demande names is no field of the server's (one gift per version, I-4 D1). */
function askBody(a: Ask) {
  const own = a.kind === 'quiz' ? { questions: (a.questions ?? []).map(q => ({ id: q.id, text: q.text, choices: q.choices, correct: q.correct })) }
    : a.kind === 'question' ? { question: a.question ?? '', answerType: a.answerType ?? 'choix', choices: a.answerType === 'libre' ? [] : a.choices ?? [], freeLine: !!a.freeLine }
      : { prompt: a.prompt ?? '' }
  return { name: a.name.trim(), category: a.category, endMessage: a.endMessage?.trim() || null, ...own }
}

/** The workspace's fields this edit changed. The look is the brand, sent whole (the server replaces it whole): the logo,
    the roue's look, and the one colour the customer page wears (D-86), the Marque colour. */
function workspaceBody(fields: Array<'sector' | 'identityFields' | 'wheelDesign'>, w: ServerWorkspace) {
  const d = w.wheelDesign
  return {
    ...(fields.includes('sector') && w.sector ? { sector: w.sector } : {}),
    ...(fields.includes('identityFields') ? { identityFields: { prenom: !!w.identityFields.prenom, telephone: !!w.identityFields.telephone } } : {}),
    ...(fields.includes('wheelDesign') ? { brand: d
      ? { logoUrl: d.logoUrl ?? null, wheelDesign: { preset: d.preset, parts: d.parts, a: d.a, b: d.b, logo: d.logo }, colour: /^#[0-9a-fA-F]{6}$/.test(d.a) ? d.a : null }
      : { logoUrl: null, wheelDesign: null, colour: null } } : {}),
  }
}

/* ---------- the writes, one per edit ---------- */
function sendType(id0: string) {
  send([`type:${id0}`], async () => {
    const t = mirror().types.find(x => x.id === serverIdOf(id0))
    if (!t) return
    if (onServer(t.id)) {
      /* made already, ahead of a roue that holds it (typeFirst), with what the tab holds now: nothing more to send */
      const first = madeFirst.get(t.id); madeFirst.delete(t.id)
      if (first === JSON.stringify(typeBody(t))) return
      answer('types', t, typeOf(await call<AppGiftType>('PATCH', `/app/gift-types/${enc(t.id)}`, typeBody(t)))); return
    }
    await makeType(t)
  }, () => backType(id0))
}
/** POST a gift type made in this tab; the tab then names it by the server's id. Answers the body of what the tab now holds:
    the server's answer, or (changed while on its way) what was sent, so that the change is sent next. */
async function makeType(t: GiftType): Promise<string> {
  const body = typeBody(t)
  const r = await call<AppGiftType>('POST', '/app/gift-types', body)
  const same = mirror().types.find(x => x.id === t.id) === t
  rekey(t.id, r.id)
  if (!same) return JSON.stringify(body)
  const got = typeOf(r)
  answer('types', mirror().types.find(x => x.id === r.id)!, got)
  return JSON.stringify(typeBody(got))
}
/** One gesture edits the gifts, then the roue that holds them (the guide's « Garder », a gift added to a roue), and the
    roue's write can come first in the queue: a gift made in this tab that a roue holds goes to the server before that
    roue (an unknown rewardId is refused, 03 §3.2). Its own write, next in the queue, then has nothing left to send. Answers
    the gifts the server refused: a roue holding one is not sent. */
const madeFirst = new Map<string, string>()
async function typesFirst(): Promise<Map<string, unknown>> {
  const lost = new Map<string, unknown>()
  for (const id of new Set(mirror().roues.flatMap(w => w.rewards.map(x => x.rewardId)))) {
    const t = onServer(id) ? undefined : mirror().types.find(x => x.id === id)
    if (!t) continue
    try {
      const body = await makeType(t)
      madeFirst.set(serverIdOf(id), body)
    } catch (e) { lost.set(id, e) }
  }
  return lost
}

/** Every roue of the tab against what the server holds: made here → POST, changed → PATCH, gone → archive. A roue the
    server refuses does not stop the others: each is sent, then the first refusal is said, and only the refused are read
    back (R1). The write concerns the roues this edit changed. */
function syncRoues(before: Roue[]) {
  let refused: string[] = []
  held ??= new Map(before.filter(w => onServer(w.id)).map(w => [w.id, heldOf(w)]))
  const now = mirror().roues, was = new Map(before.map(w => [w.id, JSON.stringify(w)]))
  const changed = [...now.filter(w => was.get(w.id) !== JSON.stringify(w)), ...before.filter(w => !now.some(x => x.id === w.id))]
  send(changed.map(w => `roue:${w.id}`), async () => {
    refused = []
    const errors: unknown[] = []
    const no = (id: string, e: unknown) => { refused.push(id); errors.push(e) }
    const lost = await typesFirst()
    for (const w of mirror().roues) {
      const gift = w.rewards.find(x => lost.has(x.rewardId))
      if (gift) { no(w.id, lost.get(gift.rewardId)); continue }
      const body = roueBody(w)
      try {
        if (!onServer(w.id)) {
          const r = await call<AppRoue>('POST', '/app/roues', body)
          const same = mirror().roues.find(x => x.id === w.id) === w
          rekey(w.id, r.id)
          const got = roueOf(r); held!.set(r.id, heldOf(got))
          if (same) answer('roues', mirror().roues.find(x => x.id === r.id)!, got)
        } else if (held!.get(w.id) !== JSON.stringify(body)) {
          const got = roueOf(await call<AppRoue>('PATCH', `/app/roues/${enc(w.id)}`, body))
          held!.set(w.id, heldOf(got)); answer('roues', w, got)
        }
      } catch (e) { no(w.id, e) }
    }
    for (const id of [...held!.keys()]) {
      if (mirror().roues.some(w => w.id === id)) continue
      try {
        await call<AppRoue>('POST', `/app/roues/${enc(id)}/archive`, {})
        held!.delete(id)
      } catch (e) { no(id, e) }
    }
    if (errors.length) throw errors[0]
  }, () => backRoues(refused))
}

function sendAsk(id0: string) {
  send([`ask:${id0}`], async () => {
    const a = mirror().asks.find(x => x.id === serverIdOf(id0))
    if (!a) return
    if (onServer(a.id)) { answer('asks', a, askOf(await call<AppAsk>('PATCH', `/app/asks/${enc(a.id)}`, askBody(a)))); return }
    const r = await call<AppAsk>('POST', '/app/asks', { kind: a.kind, ...askBody(a) })
    const same = mirror().asks.find(x => x.id === a.id) === a
    rekey(a.id, r.id)
    if (same) answer('asks', mirror().asks.find(x => x.id === r.id)!, askOf(r))
  }, () => backAsk(id0))
}
function archiveAsk(id0: string) {
  send([`ask:${id0}`], async () => {
    const id = serverIdOf(id0)
    if (onServer(id)) await call<AppAsk>('POST', `/app/asks/${enc(id)}/archive`, {})
  }, () => backAsk(id0))
}

/** A platform linked (POST …/platforms, one row per platform and establishment: a second custom link replaces the
    first, as the server keeps it). Google comes from the fiche (02 §2.2): its link goes there. */
function sendPlatform(ref: Extract<Edit, { kind: 'platform' }>) {
  const { locationId, platform } = ref.ref
  if (platform === 'google') { sendFiche(locationId, ref.ref.url ?? ''); return }
  send([`platform:${locationId}:${platform}`], async () => {
    const d = mirror().destinations.find(x => x.locationId === locationId && x.platform === platform && (platform !== 'custom' || x.url === ref.ref.url))
    if (!d) return
    const row = await call<AppPlatform>('POST', `/app/locations/${enc(locationId)}/platforms`,
      { platform, url: d.url ?? '', ...(platform === 'custom' ? { name: d.name ?? '' } : {}), icon: d.icon ?? null })
    const list = mirror().destinations
    if (list.includes(d)) setMirror({ destinations: [...list.filter(x => !(x.locationId === locationId && x.platform === platform)), destinationOf(row)] })
  }, () => backPlatform(locationId, platform))
}
function dropPlatform(ref: Extract<Edit, { kind: 'platform-drop' }>) {
  const { locationId, platform } = ref.ref
  send([`platform:${locationId}:${platform}`], async () => {
    /* the same platform linked again since: nothing to drop */
    if (mirror().destinations.some(x => x.locationId === locationId && x.platform === platform)) return
    await call<void>('DELETE', `/app/locations/${enc(locationId)}/platforms/${enc(platform)}`)
      .catch(e => { if (!(e instanceof ApiError && e.status === 404)) throw e })
  }, () => backPlatform(locationId, platform))
}
function sendSocial(locationId: string, social: string) {
  send([`social:${locationId}:${social}`], async () => {
    const s = mirror().socials.find(x => x.locationId === locationId && x.social === social)
    if (!s) return
    const row = await call<AppSocial>('POST', `/app/locations/${enc(locationId)}/socials`, { social, url: s.url, handle: s.handle ?? null, followers: s.followers ?? null })
    const list = mirror().socials
    if (list.includes(s)) setMirror({ socials: list.map(x => (x === s ? socialOf(row) : x)) })
  }, () => backSocial(locationId, social))
}
function dropSocial(locationId: string, social: string) {
  send([`social:${locationId}:${social}`], async () => {
    if (mirror().socials.some(x => x.locationId === locationId && x.social === social)) return
    await call<void>('DELETE', `/app/locations/${enc(locationId)}/socials/${enc(social)}`)
      .catch(e => { if (!(e instanceof ApiError && e.status === 404)) throw e })
  }, () => backSocial(locationId, social))
}

/** The establishment's fields the server takes (PATCH /v1/app/locations/:id): its name, the address typed by hand (a
    later fiche read that brings an address still overwrites it, 02 §2.1), its booking link, its window; lib-pack-22: its
    « Lien d'avis Google » on its own (02 §2.2 reviewUrl; null = back to the fiche's). */
const LOCATION_FIELDS = ['name', 'address', 'bookingUrl', 'windowDays', 'reviewUrl'] as const
function sendLocation(id: string, fields: LocationField[]) {
  const sent = LOCATION_FIELDS.filter(f => fields.includes(f))
  if (!sent.length) return
  send(fields.map(f => `location:${id}:${f}`), async () => {
    const l = mirror().locations.find(x => x.id === id)
    if (!l) return
    const r = locationOf(await call<ServerLocation>('PATCH', `/app/locations/${enc(id)}`, Object.fromEntries(sent.map(f => [f, l[f]]))))
    const now = mirror().locations.find(x => x.id === id)
    if (!now) return
    const locations = mirror().locations.map(x => (x.id !== id ? x
      : { ...x, status: r.status, rating: r.rating, ...Object.fromEntries(sent.filter(f => now[f] === l[f]).map(f => [f, r[f]])) }))
    setMirror({ locations }); qc?.setQueryData(['locations'], locations)
  }, () => readLocations({ id, fields: sent }))
}
function sendWorkspace(fields: WorkspaceField[]) {
  send(fields.map(f => `workspace:${f}`), async () => {
    const w = mirror().workspace
    if (!w) return
    const r = workspaceOf(await call<AppWorkspace>('PATCH', '/app/workspace', workspaceBody(fields, w)))
    const now = mirror().workspace
    if (now) setMirror({ workspace: { ...now, ...Object.fromEntries(fields.filter(f => now[f] === w[f]).map(f => [f, r[f]])) } })
  }, () => readWorkspace(fields))
}
/** A Google link pasted on step 1 (POST …/fiche): what the server read, its Google platform and the establishment's link. */
function sendFiche(locationId: string, url: string) {
  send([`fiche:${locationId}`, `platform:${locationId}:google`], async () => {
    const l = mirror().locations.find(x => x.id === locationId)
    if (!l) return
    const f = await call<AppFiche>('POST', `/app/locations/${enc(locationId)}/fiche`, { url })
    setMirror({ fiches: { ...mirror().fiches, [locationId]: ficheOf(f, l) } })
    await Promise.all([readPlatforms(), readLocations()])
  }, async () => { await Promise.all([readFiche(locationId), backPlatform(locationId, 'google')]) })
}
/** The day-one model (POST /onboarding/model): the server prepares the gifts, the roue, the quiz, the window and the
    sector in one call; they are read back, and the new campaign starts from that roue. The API's model predates the
    gifts' one-line conditions (lib-pack-8): the model's gifts the server made with none take the model's own, once,
    here (a gap said in the 2b-2 HANDOFF; with the API's port these writes send nothing). */
function sendModel(sector: string, locationId: string) {
  send([`model:${locationId}`], async () => {
    const r = await call<{ giftTypes: number; roueId: string; quizId: string }>('POST', '/app/onboarding/model', { sector, locationId })
    await Promise.all([readTypes(), readRoues(), readAsks(), readLocations(), readWorkspace()])
    setMirror({ activeRoueId: r.roueId })
    const model = MODELS[sector as SecteurId], made = mirror().roues.find(w => w.id === r.roueId)?.rewards.map(x => x.rewardId) ?? []
    for (const t of mirror().types.filter(x => made.includes(x.id) && !x.condition)) {
      const condition = model?.gifts.find(g => g.name === t.name)?.condition
      if (condition) answer('types', t, typeOf(await call<AppGiftType>('PATCH', `/app/gift-types/${enc(t.id)}`, typeBody({ ...t, condition }))))
    }
  }, async () => { await Promise.all([readTypes(), readRoues(), readAsks(), readWorkspace(), readLocations()]) })
}

/** lib-pack-19 · the library of saved avis / social presets, replaced whole (PUT /v1/app/library, 04 §4.4): the tab's
    list as it is when its turn comes; the server's answer (the stored list) in its place unless the list changed again
    meanwhile (its own write follows). A refusal reads the server's list back, under the same rule. */
const fetchLibrary = async () => (await get<{ items: LinkAsk[] }>('/app/library')).items.map(linkAskOf)
function sendLibrary() {
  send(['library:all'], async () => {
    const sent = mirror().library
    const r = await call<{ items: LinkAsk[] }>('PUT', '/app/library', { items: sent.map(linkAskOf) })
    if (mirror().library === sent) setMirror({ library: r.items.map(linkAskOf) })
  }, async () => { if (later('library', 'all')) return; setMirror({ library: await fetchLibrary() }) })
}

/* ---------- lib-pack-21 · « Étiquettes » (02 §2.5, 0036) ---------- */
/** The list as the server holds it, in its order; `later`: a write asked since will send the tab's. */
const fetchTags = async () => (await get<{ items: AppTag[] }>('/app/tags')).items.map(tagOf)
async function readTags() { if (later('tags', 'all')) return; setMirror({ tags: await fetchTags() }) }
/** The whole list, replaced in one PUT (the server diffs it: a rename keeps the items, a dropped id is « Supprimer »).
    The body carries only id and name (position = the order sent); the answer, with the server's counts, takes the list's
    place unless it changed again meanwhile (its own write follows). */
function sendTags() {
  send(['tags:all'], async () => {
    const sent = mirror().tags
    const r = await call<{ items: AppTag[] }>('PUT', '/app/tags', { items: sent.slice().sort((a, b) => a.position - b.position).map(t => ({ id: t.id, name: t.name })) })
    if (mirror().tags === sent) setMirror({ tags: r.items.map(tagOf) })
  }, readTags)
}
/** « Fusionner »: POST …/tags/:from/merge { into }; the answer is the list after it. A refusal reads the list back and the
    items that carried the source (they still do on the server). */
function sendMerge(from: string, into: string) {
  send(['tags:all'], async () => {
    const sent = mirror().tags
    const r = await call<{ items: AppTag[] }>('POST', `/app/tags/${enc(from)}/merge`, { into })
    if (mirror().tags === sent) setMirror({ tags: r.items.map(tagOf) })
  }, async () => { await readTags(); await readItemTags() })
}
/** An item's étiquettes: PATCH { tags } on the campaign or the support (row data, never its document). An item not on
    the server yet (a draft not saved) sends nothing: its first POST carries them (lib/server › saveDraft). The answer's
    row notes what the server holds; the counts are read again with it. */
function sendItemTags(item: TagKind, id0: string) {
  send([`item-tags:${item}:${id0}`], async () => {
    const id = serverIdOf(id0)
    if (!onServer(id)) return
    const tags = (item === 'campaign' ? mirror().itemTags.campaigns : mirror().itemTags.supports)[id] ?? []
    const path = item === 'campaign' ? `/app/campaigns/${enc(id)}` : `/app/supports/${enc(id)}`
    /* the campaign's answer is { campaign } (05 §5.3); a support's is the support itself (06 §6.3) */
    const held = item === 'campaign' ? (await call<{ campaign: CampaignRow }>('PATCH', path, { tags })).campaign.tags : (await call<AppSupport>('PATCH', path, { tags })).tags
    if (!later('item-tags', `${item}:${id0}`)) noteItemTags(item, id, [...(held ?? [])])
    await readTags()
  }, () => backItemTags(item, id0))
}
/** A refused item write: what the server holds of that item, read back (its row), unless a write asked since will send. */
async function backItemTags(item: TagKind, id0: string) {
  if (later('item-tags', `${item}:${id0}`)) return
  const id = serverIdOf(id0)
  if (!onServer(id)) return
  if (item === 'campaign') { const r = await readCampaign(id).catch(() => null); if (r) noteItemTags('campaign', id, [...(r.campaign.tags ?? [])]) }
  else { const s = (await get<AppSupport[]>('/app/supports')).find(x => x.id === id); if (s) noteItemTags('support', id, [...(s.tags ?? [])]) }
  await readTags()
}
/** Every item's étiquettes read again (after a merge the server refused): the campaigns' rows and the supports' list. */
async function readItemTags() {
  const [rows, supports] = await Promise.all([get<CampaignRow[]>('/app/campaigns'), get<AppSupport[]>('/app/supports')])
  for (const r of rows) if (!later('item-tags', `campaign:${r.id}`)) noteItemTags('campaign', r.id, [...(r.tags ?? [])])
  for (const s of supports) if (!later('item-tags', `support:${s.id}`)) noteItemTags('support', s.id, [...(s.tags ?? [])])
}

/* ---------- lib-pack-23 · « Catégories » and « Emplacements » (02 §2.6, 0039) — the étiquettes' wire, one value per item ---------- */
type OrgList = 'categories' | 'places'
type OrgWire = Array<AppCategory & AppPlace>
/** The server's list as the mirror holds it, under its own key. */
const orgMirror = (k: OrgList, wire: OrgWire): Partial<Mirror> =>
  k === 'categories' ? { categories: wire.map(categoryOf) } : { places: wire.map(placeOf) }
async function readOrg(k: OrgList) {
  if (later(k, 'all')) return
  setMirror(orgMirror(k, (await get<{ items: OrgWire }>(`/app/${k}`)).items))
}
/** The whole list, replaced in one PUT (the server diffs it: a rename keeps the items, a dropped id is « Supprimer », its
    items at none). Only id and name travel (position = the order sent); the answer, with the server's counts, takes the
    list's place unless it changed again meanwhile (its own write follows). */
function sendOrg(k: OrgList) {
  send([`${k}:all`], async () => {
    const sent = mirror()[k]
    const r = await call<{ items: OrgWire }>('PUT', `/app/${k}`, { items: sent.slice().sort((a, b) => a.position - b.position).map(t => ({ id: t.id, name: t.name })) })
    if (mirror()[k] === sent) setMirror(orgMirror(k, r.items))
  }, () => readOrg(k))
}
/** « Fusionner »: POST …/:from/merge { into }; the answer is the list after it. A refusal reads the list back and the items. */
function sendOrgMerge(k: OrgList, from: string, into: string) {
  send([`${k}:all`], async () => {
    const sent = mirror()[k]
    const r = await call<{ items: OrgWire }>('POST', `/app/${k}/${enc(from)}/merge`, { into })
    if (mirror()[k] === sent) setMirror(orgMirror(k, r.items))
  }, async () => { await readOrg(k); await readOrgItems() })
}
/** A campaign's catégorie: PATCH { categoryId } on the campaign (row data, never its document). A draft not on the server
    yet sends nothing: its first POST carries it (lib/server › saveDraft). The answer's row notes what the server holds. */
function sendCampaignCategory(id0: string) {
  send([`item-category:${id0}`], async () => {
    const id = serverIdOf(id0)
    if (!onServer(id)) return
    const categoryId = mirror().itemCategory[id] ?? null
    const held = (await call<{ campaign: CampaignRow }>('PATCH', `/app/campaigns/${enc(id)}`, { categoryId })).campaign.categoryId ?? null
    if (!later('item-category', id0)) noteCampaignCategory(id, held)
    await readOrg('categories')
  }, async () => {
    if (later('item-category', id0)) return
    const id = serverIdOf(id0)
    if (!onServer(id)) return
    const r = await readCampaign(id).catch(() => null); if (r) noteCampaignCategory(id, r.campaign.categoryId ?? null)
    await readOrg('categories')
  })
}
/** A support's emplacement: PATCH { placeId } on the support; the supports' list is read again (its rows say the name). */
function sendSupportPlace(id0: string) {
  send([`item-place:${id0}`], async () => {
    const id = serverIdOf(id0)
    if (!onServer(id)) return
    const placeId = mirror().itemPlace[id] ?? null
    const held = (await call<AppSupport>('PATCH', `/app/supports/${enc(id)}`, { placeId })).placeId ?? null
    if (!later('item-place', id0)) noteSupportPlace(id, held)
    void qc?.invalidateQueries({ queryKey: ['supports'] })
    await readOrg('places')
  }, async () => {
    if (later('item-place', id0)) return
    const id = serverIdOf(id0)
    const s = (await get<AppSupport[]>('/app/supports')).find(x => x.id === id); if (s) noteSupportPlace(id, s.placeId ?? null)
    await readOrg('places')
  })
}
/** Every item's value read again (after a merge the server refused): the campaigns' rows and the supports' list. */
async function readOrgItems() {
  const [rows, supports] = await Promise.all([get<CampaignRow[]>('/app/campaigns'), get<AppSupport[]>('/app/supports')])
  for (const r of rows) if (!later('item-category', r.id)) noteCampaignCategory(r.id, r.categoryId ?? null)
  for (const s of supports) if (!later('item-place', s.id)) noteSupportPlace(s.id, s.placeId)
}

setEditSink(e => {
  switch (e.kind) {
    case 'type': return sendType(e.id)
    case 'roues': return syncRoues(e.before)
    case 'ask': return sendAsk(e.id)
    case 'ask-archive': return archiveAsk(e.id)
    case 'library': return sendLibrary()
    case 'tags': return sendTags()
    case 'tag-merge': return sendMerge(e.from, e.into)
    case 'item-tags': return sendItemTags(e.item, e.id)
    case 'categories': case 'places': return sendOrg(e.kind)
    case 'category-merge': return sendOrgMerge('categories', e.from, e.into)
    case 'place-merge': return sendOrgMerge('places', e.from, e.into)
    case 'item-category': return sendCampaignCategory(e.id)
    case 'item-place': return sendSupportPlace(e.id)
    case 'platform': return sendPlatform(e)
    case 'platform-drop': return dropPlatform(e)
    case 'social': return sendSocial(e.locationId, e.social)
    case 'social-drop': return dropSocial(e.locationId, e.social)
    case 'location': return sendLocation(e.id, e.fields)
    case 'workspace': return sendWorkspace(e.fields)
    case 'fiche': return sendFiche(e.locationId, e.url)
    case 'model': return sendModel(e.sector, e.locationId)
  }
})

/* ---------- the app's sentences for a refused edit (C-2: the API sends codes) ---------- */
/** The first line is the one a toast says. Lines the app already writes for the same check are reused. */
export function editRefusalLines(e: unknown): string[] {
  if (!(e instanceof ApiError)) return ['Le serveur ne répond pas : rien n’a changé. Vérifiez la connexion, puis réessayez.']
  if (e.status === 401) return ['Votre session a expiré : reconnectez-vous, puis réessayez.']
  switch (e.code) {
    /* the manager pack (12/10): a Collaborateur's write of a cadeau, a roue or a demande; the other writes stay owner/admin */
    case 'forbidden_role': {
      const k = kindOf.get(e) ?? ''
      return [k === 'type' || k === 'roues' ? 'Votre rôle ne permet pas de gérer les cadeaux et les roues\u00a0: rien n’a changé.'
        : k === 'ask' || k === 'ask-archive' || k === 'library' ? 'Votre rôle ne permet pas de gérer les demandes\u00a0: rien n’a changé.'
        /* lib-pack-21 · the list (owner, admin, a manager over every establishment); an item's own follow the item's rights */
        : k === 'tags' ? `${TAG_ROLE_LINE.replace(/\.$/, '')}\u00a0: rien n’a changé.`
        /* lib-pack-23 · the two other lists, the same rule */
        : k === 'categories' ? `${CATEGORY_ROLE_LINE.replace(/\.$/, '')}\u00a0: rien n’a changé.`
        : k === 'places' ? `${PLACE_ROLE_LINE.replace(/\.$/, '')}\u00a0: rien n’a changé.`
        : k === 'item-tags' || k === 'item-category' || k === 'item-place' ? 'Votre rôle ne permet pas de modifier cet élément\u00a0: rien n’a changé.'
        : 'Seuls le propriétaire et les administrateurs modifient ces réglages : rien n’a changé.']
    }
    case 'out_of_scope': return ['Ce que vous modifiez touche un établissement qui ne fait pas partie de vos accès\u00a0: rien n’a changé.']
    case 'no_location': return ['Choisissez au moins un de vos établissements.']
    case 'roue_in_use': return ['Une campagne en ligne utilise cette roue : elle reste dans vos roues.']
    case 'ask_in_use': return ['Une campagne en ligne pose cette demande : elle reste dans vos demandes.']
    /* lib-pack-19 · the library presets row (04 §4.4); lib-pack-21 · the same codes on the étiquettes list (02 §2.5) */
    case 'name_ties_gift': { const k = kindOf.get(e); return [k === 'tags' ? TAG_NAME_TIES_GIFT : k === 'categories' ? CATEGORY_NAME_TIES_GIFT : NAME_TIES_GIFT] }
    case 'too_many_items': { const k = kindOf.get(e); return [k === 'tags' ? TAG_CAP_LINE : k === 'categories' ? CATEGORY_CAP_LINE : k === 'places' ? PLACE_CAP_LINE : 'Cinquante demandes enregistrées au plus\u00a0: rien n’a changé.'] }
    case 'duplicate_name': { const k = kindOf.get(e); return [k === 'categories' ? CATEGORY_NAME_TAKEN : k === 'places' ? PLACE_NAME_TAKEN : TAG_NAME_TAKEN] }
    /* lib-pack-23 · 0039 */
    case 'unknown_category': return ['Cette catégorie n’existe plus sur le serveur\u00a0: rechargez la page.']
    case 'unknown_place': return ['Cet emplacement n’existe plus sur le serveur\u00a0: rechargez la page.']
    case 'too_many_places': return [PLACE_CAP_LINE]
    case 'too_many_tags': return [TAG_PER_ITEM_LINE]
    case 'unknown_tag': return ['Cette étiquette n’existe plus sur le serveur\u00a0: rechargez la page.']
    case 'platform_shares_not_100': case 'invalid_share': return ['La répartition doit faire 100 %.']
    case 'platform_in_use': return ['Une campagne en ligne invite à cette plateforme : elle reste liée.']
    case 'gift_type_in_use': return ['Une campagne en ligne propose ce cadeau : il reste dans vos cadeaux.']
    case 'name_taken': return ['Une roue porte déjà ce nom dans cet établissement : choisissez-en un autre.']
    case 'unwinnable_slice': return ['Un poids nul n’est pas publiable — chaque part visible reste gagnable.']
    case 'unknown_gift_type': return ['Un cadeau de cette roue n’existe plus : retirez-le, puis enregistrez.']
    case 'name_ties_review': return ['Le nom d’un cadeau ne parle jamais d’avis.']
    case 'condition_ties_review': return ['Une condition ne parle jamais d’avis.']
    case 'end_message_ties_gift': return ['Le message de fin remercie, sans parler du cadeau.']
    case 'links_tie_review': return [BOOKING_REVIEW]
    case 'below_sector_floor': { const n = sectorFloor(); return [`Pour votre secteur, la fenêtre est d’au moins ${n} jour${n > 1 ? 's' : ''}.`] }
    case 'google_from_fiche': return ['Le lien Google vient de la fiche : pour le changer, liez une autre fiche.']
    case 'not_google': return [e.field === 'url' ? FICHE_NOT_GOOGLE : REVIEW_URL_NOT_GOOGLE] /* lib-pack-22 · 02 §2.2: the fiche's link, or the review link */
    case 'place_not_found': return ['Cette fiche Google est introuvable : vérifiez le lien, puis réessayez.']
    case 'not_found': case 'archived': return ['Ce que vous modifiez n’existe plus sur le serveur : rechargez la page.']
  }
  if (e.code === 'invalid_field' && (e.field === 'bookingUrl' || e.field === 'url')) return ['Un lien qui commence par https://, ou rien.']
  if (e.code === 'invalid_field' && e.field === 'reviewUrl') return [REVIEW_URL_INVALID] /* lib-pack-22 · https, 500 at most; null needs a place id */
  if (e.code === 'invalid_field' && /^items\.\d+\.name$/.test(e.field ?? '')) {
    const k = kindOf.get(e)
    if (k === 'tags') return [TAG_NAME_LONG]
    if (k === 'categories') return [CATEGORY_NAME_LONG]
    if (k === 'places') return [PLACE_NAME_LONG]
  }
  return ['L’action n’a pas abouti : rien n’a changé. Réessayez dans un instant.']
}
/** MA-17 · a booking link that opens a page for writing a review (bookingUrlCheck › reviewPage; the API's links_tie_review). */
export const BOOKING_REVIEW = 'Ce lien ouvre une page d’avis : le lien de rendez-vous s’affiche avec le cadeau, qui reste disponible avec ou sans avis.'

/** At the app's root (App.tsx): the establishments an answer changes reach useLocations, and a refusal no page asked
    for is said. Renders nothing; the demo does nothing here. */
export function ServerWrites() {
  const client = useQueryClient()
  const toast = useToast()
  useEffect(() => {
    if (!apiWrites) return
    qc = client
    const hear = () => {
      /* after the writes still on their way, and after the page that asked has had its say (settled) */
      void queueWrite(async () => undefined).then(() => setTimeout(() => {
        const open = refusals.filter(r => !r.said)
        open.forEach(r => { r.said = true })
        if (open[0]) toast({ message: editRefusalLines(open[0].error)[0] })
      }, 0))
    }
    heard.add(hear)
    return () => { heard.delete(hear) }
  }, [client, toast])
  return null
}
